import React from "react";
import clsx from "clsx";
import { Popover, Menu, MenuItem, CircularProgress } from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { toast } from "react-toastify";
import { MI } from "./icons";
import { useStyles } from "./styles";
import { COLORS, STAGES, TONE } from "./theme";
import { ROLES, STATUS } from "./mockData";
import { Role, Row, StatusKey } from "./types";
import {
  AgendaController,
  copyFor,
  doneRows,
  rowsForRole,
} from "./useAgendaState";
import { CheckList } from "../reportWritersAgenda/CheckList";
import { formatDate } from "../../../common/formatDate";
import { NotesDialog } from "./NotesDialog";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import { useFetchContext } from "../../../common/FetchContext";

/* ────────────────────────────────── stage cell ────────────────────────────────── */
function PdfPreviewDialog({
  pdfUrl,
  fileName,
  open,
  onClose,
}: {
  pdfUrl: string | null;
  fileName: string;
  open: boolean;
  onClose: () => void;
}) {
  const handleDownload = () => {
    if (!pdfUrl) return;

    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = fileName || "report.pdf";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{fileName || "Report PDF"}</DialogTitle>

      <DialogContent style={{ height: "80vh", padding: 0 }}>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="PDF Preview"
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleDownload} color="primary" disabled={!pdfUrl}>
          Download
        </Button>

        <Button onClick={onClose} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Pips({ st }: { st: StatusKey }) {
  const c = useStyles();
  const s = STATUS[st];
  return (
    <div className={c.pips}>
      {STAGES.map((_, i) => {
        const cls = [c.pip];
        if (i < s.stage) cls.push(c.pipDone);
        else if (i === s.stage) {
          if (s.back) cls.push(c.pipBack);
          else if (st === "approved") cls.push(c.pipDone);
          else cls.push(c.pipNow);
        }
        return <div key={i} className={clsx(cls)} />;
      })}
    </div>
  );
}

function NeedLine({
  r,
  role,
  isCopy,
  needText,
}: {
  r: Row;
  role: Role;
  isCopy: boolean;
  needText: string;
}) {
  const c = useStyles();
  const s = STATUS[r.st];
  if (isCopy)
    return (
      <div className={c.stageNeed}>
        <MI name="eye" size={13} /> Reference copy — no action required
      </div>
    );
  if (!s.holder)
    return (
      <div className={c.stageNeed}>
        <MI name="lock" size={13} /> {needText}
      </div>
    );
  const mine = s.holder === role;
  return (
    <div className={c.stageNeed}>
      {mine ? (
        <>
          <MI name="playArrow" size={13} />
          <span>
            <b>Your turn</b> — {needText}
          </span>
        </>
      ) : (
        <>
          <MI name="clock" size={13} />
          <span>
            Waiting on {r.owner} to {needText}
          </span>
        </>
      )}
    </div>
  );
}

/* ────────────────────────────────── feedback cell ────────────────────────────── */

function Bar({
  num,
  lab,
  pct,
  color,
  onClick,
  cls,
  dot,
  sub,
}: {
  num: string;
  lab: string;
  pct: number;
  color: string;
  onClick: () => void;
  cls?: "warn" | "done";
  dot?: boolean;
  sub?: string;
}) {
  const c = useStyles();
  return (
    <button className={c.fb} onClick={onClick}>
      <div className={c.fbTop}>
        {dot && <span className={c.newdot} />}
        <span
          className={clsx(
            c.fbNum,
            cls === "warn" && c.fbWarnNum,
            cls === "done" && c.fbDoneNum,
          )}
        >
          {num}
        </span>
        <span className={c.fbLab}>{lab}</span>
      </div>
      <div className={c.fbBar}>
        <div
          className={c.fbFill}
          style={{ width: `${Math.round(pct * 100)}%`, background: color }}
        />
      </div>
      {sub && (
        <div className={c.fbSub}>
          <MI name="deviceFloppy" size={11} />
          {sub}
        </div>
      )}
    </button>
  );
}

function getApiProgress(r: Row) {
  if (!r.progress_bar) {
    return null;
  }

  const total = r.progress_bar.total_count ?? 0;
  const completed = r.progress_bar.completed_count ?? 0;
  const raised = r.progress_bar.raised_count ?? 0;

  if (total <= 0) {
    return null;
  }

  const safeCompleted = Math.max(0, Math.min(completed, total));
  const pct = safeCompleted / total;

  return {
    total,
    completed: safeCompleted,
    raised,
    pct,
  };
}

function FeedbackCell({
  r,
  role,
  onOpen,
}: {
  r: Row;
  role: Role;
  onOpen: (row: Row) => void;
}) {
  const c = useStyles();

  const progress = getApiProgress(r);

  if (!progress) {
    return <span className={c.fbNone}>Nothing raised</span>;
  }

  const isCopy =
    role === "verifier" && copyFor(r) && !["verify", "corr_ver"].includes(r.st);

  const { total, completed, raised, pct } = progress;

  // WRITER
  if (role === "writer") {
    if (r.st === "corr_rev") {
      return (
        <Bar
          num={`${completed} of ${total}`}
          lab="checklist notes"
          pct={pct}
          color={COLORS.amber}
          onClick={() => onOpen(r)}
          cls="warn"
          dot={raised > 0}
        />
      );
    }

    if (r.st === "corr_ver") {
      return (
        <Bar
          num={`${completed} of ${total}`}
          lab="verification comments"
          pct={pct}
          color={COLORS.amber}
          onClick={() => onOpen(r)}
          cls="warn"
          dot={raised > 0}
        />
      );
    }

    if (r.st === "corr_app") {
      return (
        <Bar
          num={`${completed} of ${total}`}
          lab="approval comments"
          pct={pct}
          color={COLORS.amber}
          onClick={() => onOpen(r)}
          cls="warn"
          dot={raised > 0}
        />
      );
    }

    return <span className={c.fbNone}>Nothing raised</span>;
  }

  // REVIEWER
  if (role === "reviewer") {
    if (r.st === "review") {
      const notesRaised = progress.raised;

      return (
        <Bar
          num={`${completed}/${total}`}
          lab="checklist"
          pct={pct}
          color={notesRaised > 0 ? COLORS.amber : COLORS.green}
          onClick={() => onOpen(r)}
          cls={notesRaised > 0 ? "warn" : "done"}
          dot={notesRaised > 0}
          sub={notesRaised > 0 ? `${notesRaised} notes raised` : undefined}
        />
      );
    }

    return (
      <Bar
        num={`${completed}/${total}`}
        lab="checklist notes fixed"
        pct={pct}
        color={COLORS.blue}
        onClick={() => onOpen(r)}
      />
    );
  }

  // VERIFIER
  if (role === "verifier") {
    if (isCopy) {
      return (
        <Bar
          num={`${completed} of ${total}`}
          lab="approval comments"
          pct={pct}
          color={COLORS.violet}
          onClick={() => onOpen(r)}
        />
      );
    }

    return (
      <Bar
        num={`${completed} of ${total}`}
        lab="verification comments"
        pct={pct}
        color={COLORS.blue}
        onClick={() => onOpen(r)}
      />
    );
  }

  // APPROVER
  if (role === "approver") {
    if (r.st === "with_writer" || r.st === "approval") {
      return (
        <Bar
          num={`${completed} of ${total}`}
          lab="comments fixed"
          pct={pct}
          color={raised > 0 ? COLORS.amber : COLORS.green}
          onClick={() => onOpen(r)}
          cls={raised > 0 ? "warn" : "done"}
          dot={raised > 0}
        />
      );
    }

    return <span className={c.fbNone}>Nothing raised</span>;
  }

  return <span className={c.fbNone}>Nothing raised</span>;
}

/* ────────────────────────────────── action cell ───────────────────────────────── */

function ActionCell({
  r,
  role,
  onCreateReport,
  onContinueReport,
  onMore,
}: {
  r: Row;
  role: Role;
  onCreateReport: (report: Row) => void;
  onContinueReport: (report: Row) => void;
  onMore: (id: number, target: HTMLElement) => void;
}) {
  const c = useStyles();
  const isCopy =
    role === "verifier" && copyFor(r) && !["verify", "corr_ver"].includes(r.st);

  let btn: React.ReactNode = null;

  if (isCopy) {
    btn = (
      <span className={clsx(c.badge, c.badgeCopy)}>
        <MI name="eye" size={12} /> Copy — view only
      </span>
    );
    return (
      <div className={c.actwrap}>
        {btn}
        <button
          className={c.iconBtn}
          onClick={() =>
            r.is_new_report ? onCreateReport(r) : onContinueReport(r)
          }
          title="Open report"
        >
          <MI name="fileText" size={16} />
        </button>
      </div>
    );
  }

  if (role === "writer") {
    if (r.st === "notstarted")
      btn = (
        <button
          className={clsx(c.act, c.actPrimary)}
          onClick={() => onCreateReport(r)}
        >
          <MI name="pencil" size={14} />
          Start writing
        </button>
      );
    else if (r.st === "writing")
      btn = (
        <button
          className={clsx(c.act, c.actPrimary)}
          onClick={() => onContinueReport(r)}
        >
          <MI name="pencil" size={14} />
          Continue
        </button>
      );
    else if (r.st === "with_reviewer") {
      btn = <span className={clsx(c.act, c.actWait)}>With reviewer</span>;
    } else if (r.st === "with_verifier") {
      btn = <span className={clsx(c.act, c.actWait)}>With verifier</span>;
    } else if (r.st === "with_approver") {
      btn = <span className={clsx(c.act, c.actWait)}>With approver</span>;
    } else
      btn = (
        <button
          className={clsx(c.act, c.actWarn)}
          onClick={() => onContinueReport(r)}
        >
          <MI name="back" size={14} />
          Work through fixes
        </button>
      );
  } else if (role === "reviewer") {
    btn =
      r.st === "review" ? (
        <button
          className={clsx(c.act, c.actPrimary)}
          onClick={() => onContinueReport(r)}
        >
          <MI name="checklist" size={14} />
          Start checklist
        </button>
      ) : r.st === "with_writer" ? (
        <span className={clsx(c.act, c.actWait)}>With writer</span>
      ) : (
        <span className={clsx(c.act, c.actWait)}>With writer</span>
      );
  } else if (role === "verifier") {
    btn =
      r.st === "verify" ? (
        <button
          className={clsx(c.act, c.actPrimary)}
          onClick={() => onContinueReport(r)}
        >
          <MI name="messagePlus" size={14} />
          Verify
        </button>
      ) : (
        <span className={clsx(c.act, c.actWait)}>With writer</span>
      );
  } else {
    if (r.st === "approved")
      btn = (
        <span
          className={c.badge}
          style={{ background: COLORS.greenSoft, color: COLORS.green }}
        >
          <MI name="fileCheck" size={12} /> PDF generated
        </span>
      );
    else
      btn =
        r.st === "approval" ? (
          <button
            className={clsx(c.act, c.actPrimary)}
            onClick={() => onContinueReport(r)}
          >
            <MI name="stamp" size={14} />
            Approve
          </button>
        ) : (
          <span className={clsx(c.act, c.actWait)}>With writer</span>
        );
  }

  return (
    <div className={c.actwrap}>
      {btn}
      <button
        className={c.iconBtn}
        onClick={() =>
          r.is_new_report ? onCreateReport(r) : onContinueReport(r)
        }
        title="Open report"
      >
        <MI name="fileText" size={16} />
      </button>
      <button
        className={c.more}
        onClick={(e) => onMore(r.id, e.currentTarget)}
        title="More"
        disabled
      >
        <MI name="dots" size={17} />
      </button>
    </div>
  );
}

/* ────────────────────────────────── main grid view ────────────────────────────── */

interface Props {
  ctl: AgendaController;
  eNum: (r: Row) => string;
  unreadFor: (r: Row) => boolean;
  onCreateReport: (report: Row) => void;
  onContinueReport: (report: Row) => void;
  loading?: boolean;
  completedRows: Row[];
}

export const GridView: React.FC<Props> = ({
  ctl,
  eNum,
  unreadFor,
  onCreateReport,
  onContinueReport,
  loading = false,
  completedRows,
}) => {
  const c = useStyles();
  const { state, dispatch, ckPending, cmSent, cmAck } = ctl;
  const { role, query } = state;
  const { authAxios } = useFetchContext();

  const [bellAnchor, setBellAnchor] = React.useState<HTMLElement | null>(null);
  const [moreAnchor, setMoreAnchor] = React.useState<HTMLElement | null>(null);
  const [moreRow, setMoreRow] = React.useState<Row | null>(null);
  const [page, setPage] = React.useState(1);
  const [ChecklistRow, setChecklistRow] = React.useState<Row | null>(null);
  const [notesRow, setNotesRow] = React.useState<Row | null>(null);
  const [pdfOpen, setPdfOpen] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = React.useState("");

  const handleViewPdf = async (r: Row) => {
    if (!r.pdf_url) {
      toast.error("PDF URL is not available");
      return;
    }

    try {
      setPdfLoading(true);

      const response = await authAxios.get(r.pdf_url, {
        responseType: "blob",
      });

      const url = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );

      setPdfUrl(url);
      setPdfFileName(r.file_name || "report.pdf");
      setPdfOpen(true);
    } catch (error) {
      console.error("Failed to load PDF:", error);
      toast.error("Unable to load PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdf = () => {
    setPdfOpen(false);

    setPdfUrl((previousUrl) => {
      if (previousUrl) {
        URL.revokeObjectURL(previousUrl);
      }
      return null;
    });
  };

  const itemsPerPage = 10;

  const rows = rowsForRole(state.rows, role, query);
  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const pageRows = rows.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const done = role === "writer" ? completedRows : [];

  const returned = state.rows.filter(
    (r) => typeof r.st === "string" && r.st.startsWith("corr_") && unreadFor(r),
  );
  const unreadCount = role === "writer" ? returned.length : 0;

  /* need line — call with the right context per row */

  const isCopyRow = (r: Row) =>
    role === "verifier" && copyFor(r) && !["verify", "corr_ver"].includes(r.st);

  const openReport = (report: Row) =>
    report.is_new_report ? onCreateReport(report) : onContinueReport(report);

  /* ─────────────────────────────────────────────────────────────────────
   * Single source of truth for the table's column structure.
   * Both <thead> and <tbody> are rendered by mapping over this SAME array,
   * so the header and body can never drift out of sync — adding, removing,
   * or reordering a column here is the only place that needs to change.
   * columnCount / table min-width are both derived from it, never hardcoded.
   * ───────────────────────────────────────────────────────────────────── */
  type ColumnDef = {
    key: string;
    width: number;
    align?: "right";
    header: React.ReactNode;
    renderCell: (r: Row) => React.ReactNode;
  };

  const feedbackLabel =
    role === "writer"
      ? "To acknowledge"
      : role === "reviewer"
        ? "Checklist"
        : "Comments";

  const columns: ColumnDef[] = [
    {
      key: "project",
      width: 180,
      header: (
        <span className={c.thlab}>
          <MI name="folders" size={14} style={{ opacity: 0.75 }} />
          Project
        </span>
      ),
      renderCell: (r) => (
        <>
          <div className={c.pnum}>{r.proj}</div>
          <div className={c.pcust}>{r.cust}</div>
          <div className={c.pbom}>
            <MI name="stack" size={12} />
            {r.bom}
          </div>
        </>
      ),
    },
    {
      key: "report",
      width: 158,
      header: (
        <span className={c.thlab}>
          <MI name="fileText" size={14} style={{ opacity: 0.75 }} />
          Report
        </span>
      ),
      renderCell: (r) => (
        <>
          <div className={c.rtype}>{r.type}</div>
          <div className={c.rver}>{eNum(r)}</div>
        </>
      ),
    },
    {
      key: "stage",
      width: 200,
      header: (
        <span className={c.thlab}>
          <MI name="route" size={14} style={{ opacity: 0.75 }} />
          Stage
        </span>
      ),
      renderCell: (r) => {
        const s = STATUS[r.st];
        const isCopy = isCopyRow(r);
        const needText = s.need({
          pendingCount: r.progress_bar?.total_count ?? 0,
          verifyCommentCount: r.progress_bar?.total_count ?? 0,
          approvalCommentCount: r.progress_bar?.total_count ?? 0,
          role,
        });
        return (
          <div className={c.stage}>
            <Pips st={r.st} />
            <div>
              <div className={c.stageLabel}>
                <MI name={s.icon} size={14} color={TONE[s.tone]} />
                <span className={c.stageName} style={{ color: TONE[s.tone] }}>
                  {isCopy ? "With approver" : s.label}
                </span>
              </div>
              <NeedLine r={r} role={role} isCopy={isCopy} needText={needText} />
            </div>
          </div>
        );
      },
    },
    {
      key: "writerStart",
      width: 114,
      header: (
        <span className={c.thlab}>
          <MI name="calendarPlus" size={14} style={{ opacity: 0.75 }} />
          Writer start
        </span>
      ),
      renderCell: (r) => {
        const s = STATUS[r.st];
        return (
          <span className={clsx(c.plain, !r.start && c.plainNone)}>
            {r.start ? formatDate(r.start) : s.label}
          </span>
        );
      },
    },
    {
      key: "feedback",
      width: 160,
      header: (
        <span className={c.thlab}>
          <MI name="message" size={14} style={{ opacity: 0.75 }} />
          {feedbackLabel}
        </span>
      ),
      renderCell: (r) => <FeedbackCell r={r} role={role} onOpen={openReport} />,
    },
    {
      key: "notes",
      width: 110,
      header: (
        <span className={c.thlab}>
          <img
            src="/Notes.svg"
            alt="Notes"
            style={{
              width: 14,
              height: 14,
              opacity: 0.75,
            }}
          />
          NOTES
        </span>
      ),
      renderCell: (r) => {
        // Replace this with your actual note count logic from `r`
        const noteCount = r.note_count || 0;
        const hasNotes = noteCount > 0;

        return (
          <button
            type="button"
            onClick={() => setNotesRow(r)}
            title={`${noteCount} notes`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 7px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s ease",
              // Conditional Styling based on count
              border: hasNotes ? "none" : "1px solid #E5E7EA",
              backgroundColor: hasNotes ? "#16191D" : "#FFFFFF",
              color: hasNotes ? "#FFFFFF" : "#4A545F",
            }}
          >
            <img
              src="/Notes.svg"
              alt="Notes"
              style={{
                width: 15,
                height: 15,
                // Invert icon color when background is dark
                filter: hasNotes ? "invert(1) brightness(2)" : "none",
                opacity: hasNotes ? 1 : 0.95,
              }}
            />
            <span>Notes</span>

            {hasNotes && (
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#FFFFFF",
                  color: "#16191D",
                  fontSize: 11,
                  fontWeight: 700,
                  marginLeft: 2,
                }}
              >
                {noteCount}
              </span>
            )}
          </button>
        );
      },
    },

    {
      key: "due",
      width: 130,
      header: (
        <span className={c.thlab}>
          <MI name="calendarEvent" size={14} style={{ opacity: 0.75 }} />
          Due
        </span>
      ),
      renderCell: (r) => (
        <>
          <div className={clsx(c.due, r.late && c.dueLate)}>
            {r.late && <MI name="alertTriangle" size={13} />}
            {formatDate(r.due)}
          </div>
          <div className={c.dueRel}>{r.rel}</div>
        </>
      ),
    },
    {
      key: "action",
      width: 236,
      align: "right",
      header: "Action",
      renderCell: (r) => (
        <ActionCell
          r={r}
          role={role}
          onCreateReport={onCreateReport}
          onContinueReport={onContinueReport}
          onMore={(id, target) => {
            setMoreRow(r);
            setMoreAnchor(target);
          }}
        />
      ),
    },
  ];

  const columnCount = columns.length;
  const tableMinWidth = columns.reduce((sum, col) => sum + col.width, 0);

  return (
    <>
      <div className={c.shell}>
        {/* rail */}

        <main className={c.main}>
          <div className={c.head}>
            <h1>Report Management</h1>
            <span className="sub">{ROLES[role].sub}</span>
            <span className={c.spacer} />
            <button className={c.ghost}>
              <MI name="download" size={14} /> Download
            </button>
            <button className={c.ghost}>
              <MI name="upload" size={14} /> Upload Word doc
            </button>
          </div>

          {/* role tabs */}
          <div className={c.roles}>
            {state.rolesAccess.map((entry) => {
              const k = entry.role;
              const v = ROLES[k];
              if (!v) return null;
              const n = entry.count;
              const un =
                k === "writer"
                  ? state.rows.filter(
                      (x) =>
                        typeof x.st === "string" &&
                        x.st.startsWith("corr_") &&
                        x.writer === "charan.gurram" &&
                        unreadFor(x),
                    ).length
                  : 0;
              const isOn = k === role;
              const locked = !entry.access;
              return (
                <button
                  key={k}
                  className={clsx(c.roleBtn, isOn && c.roleBtnOn)}
                  style={
                    locked ? { opacity: 0.5, cursor: "not-allowed" } : undefined
                  }
                  onClick={() => {
                    if (locked) {
                      toast.error(`You don't have access to ${v.name} role`);
                      return;
                    }
                    setPage(1);
                    dispatch({ type: "SET_ROLE", role: k });
                  }}
                >
                  <MI name={locked ? "lock" : v.icon} size={15} />
                  {v.name}
                  <span className={clsx(c.roleN, isOn && c.roleNOn)}>{n}</span>
                  {un > 0 && !locked && <span className={c.tabdot} />}
                </button>
              );
            })}
          </div>

          <div className={c.rolenote}>
            <MI name="users" size={14} />
            <span>
              Viewing as{" "}
              <b style={{ color: COLORS.ink2, fontWeight: 500 }}>
                {ROLES[role].name}
              </b>{" "}
              role.
            </span>
          </div>

          <div className={c.toolbar}>
            <div className={c.search}>
              <span className="searchIcon">
                <MI name="search" size={15} />
              </span>
              <input
                placeholder="Project, customer or BOM"
                value={query}
                onChange={(e) => {
                  setPage(1);
                  dispatch({ type: "SET_QUERY", query: e.target.value });
                }}
              />
            </div>
            {/* <button className={c.ghost}>
              <MI name="filter" size={14} /> Filter
            </button> */}
            <span className={c.spacer} />
            <span className={c.hint}>
              {rows.length} of {state.rows.length} reports
            </span>
          </div>

          {/* table */}
          <div className={c.tablewrap}>
            {/* key={role} forces a full remount of the table on role change.
                table-layout:fixed derives column widths from the widths on
                the FIRST rendered row of cells; when React reconciles the
                same <table>/<thead> DOM nodes across a role switch that adds
                or removes a column, some browsers keep the previously
                computed column tracks around and the grid visibly
                misaligns. Remounting guarantees a clean layout pass every
                time, and the min-width is summed from `columns` instead of
                being a separately hand-maintained magic number per role. */}
            <table
              key={role}
              className={c.table}
              style={{ tableLayout: "fixed", minWidth: tableMinWidth }}
            >
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={c.th}
                      style={{
                        width: col.width,
                        ...(col.align === "right"
                          ? { textAlign: "right" }
                          : {}),
                      }}
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columnCount} className={c.td}>
                      <div className={c.empty} style={{ padding: "40px 0" }}>
                        <CircularProgress size={32} />
                        <p style={{ marginTop: 12 }}>Loading data...</p>
                      </div>
                    </td>
                  </tr>
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className={c.td}>
                      <div className={c.empty}>
                        <MI name="inbox" size={26} color={COLORS.ink3} />
                        <p>Nothing waiting on you</p>
                        <span>
                          Reports appear here when they reach your stage.
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pageRows.map((r) => {
                    const isCopy = isCopyRow(r);
                    const fresh =
                      role === "writer" &&
                      typeof r.st === "string" &&
                      r.st.startsWith("corr_") &&
                      unreadFor(r);
                    return (
                      <tr
                        key={r.id}
                        className={clsx(
                          c.tr,
                          isCopy && c.trCopy,
                          fresh && c.trFresh,
                        )}
                      >
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            className={c.td}
                            style={
                              col.align === "right"
                                ? { textAlign: "right" }
                                : undefined
                            }
                          >
                            {col.renderCell(r)}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {rows.length > itemsPerPage && (
            <div className={c.pager}>
              <span>
                Showing {(page - 1) * itemsPerPage + 1}–
                {Math.min(page * itemsPerPage, rows.length)} · 10 per page
              </span>
              <span style={{ flex: 1 }} />
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                size="small"
                shape="rounded"
              />
            </div>
          )}

          {/* completed section (writer only) */}
          {done.length > 0 && (
            <section className={c.doneSection}>
              <div className={c.donehead}>
                <button
                  className={c.donetoggle}
                  onClick={() => dispatch({ type: "TOGGLE_DONE_SECTION" })}
                >
                  <MI
                    name={state.doneOpen ? "chevronDown" : "chevronRight"}
                    size={17}
                    color={COLORS.ink3}
                  />
                  <MI name="circleCheck" size={17} color={COLORS.green} />
                  <span>Completed</span>
                  <span className={c.donen}>{done.length}</span>
                </button>
                <span className={c.donesub}>
                  Approved and PDF generated — read-only
                </span>
                <span style={{ flex: 1 }} />
                <button
                  className={c.ghost}
                  onClick={() =>
                    toast.success("Exporting completed report list")
                  }
                >
                  <MI name="download" size={14} /> Export list
                </button>
              </div>
              {state.doneOpen && (
                <div className={c.tablewrap}>
                  <table className={c.table}>
                    <thead>
                      <tr>
                        {[
                          { i: "folders", l: "Project" },
                          { i: "fileText", l: "Report" },
                          { i: "calendarPlus", l: "Writer start" },
                          { i: "circleCheck", l: "Completed" },
                          { i: "userCheck", l: "Approved by" },
                        ].map((h) => (
                          <th key={h.l} className={c.th}>
                            <span className={c.thlab}>
                              <MI
                                name={h.i}
                                size={14}
                                style={{ opacity: 0.75 }}
                              />
                              {h.l}
                            </span>
                          </th>
                        ))}
                        <th className={c.th} style={{ textAlign: "right" }}>
                          PDF
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {done.map((r) => (
                        <tr key={r.id} className={clsx(c.tr, c.trDone)}>
                          <td className={c.td}>
                            <div className={c.pnum}>{r.proj}</div>
                            <div className={c.pcust}>{r.cust}</div>
                            <div className={c.pbom}>
                              <MI name="stack" size={12} />
                              {r.bom}
                            </div>
                          </td>
                          <td className={c.td}>
                            <div className={c.rtype}>{r.type}</div>
                            <div className={c.rver}>{eNum(r)}</div>
                          </td>
                          <td className={c.td}>
                            <span
                              className={clsx(c.plain, !r.start && c.plainNone)}
                            >
                              {r.start ? formatDate(r.start) : "—"}
                            </span>
                          </td>
                          <td className={c.td}>
                            <span className={c.plain}>{r.doneAt ? formatDate(r.doneAt) : "—"}</span>
                          </td>
                          <td className={c.td}>
                            <span className={c.plain}>{r.approver_name}</span>
                          </td>
                          <td className={c.td} style={{ textAlign: "right" }}>
                            <div className={c.actwrap}>
                              <button
                                className={clsx(c.act, c.actSecondary)}
                                onClick={() => handleViewPdf(r)}
                                disabled={pdfLoading}
                              >
                                {pdfLoading ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <MI name="fileDownload" size={14} />
                                )}
                                {pdfLoading ? "Loading..." : "PDF"}
                              </button>
                              <button
                                className={c.iconBtn}
                                onClick={() => onContinueReport(r)}
                                title="View report"
                              >
                                <MI name="fileText" size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          <p className={c.note}>
            <b>{ROLES[role].name}.</b> {ROLES[role].note}
          </p>
        </main>
      </div>

      {/* notifications popover */}
      <Popover
        open={Boolean(bellAnchor)}
        anchorEl={bellAnchor}
        onClose={() => setBellAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ style: { minWidth: 340, padding: 6 } }}
      >
        <div
          style={{
            fontSize: 10.5,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: COLORS.ink3,
            padding: "7px 9px 4px",
          }}
        >
          {returned.length ? "Sent back to you" : "Notifications"}
        </div>
        {returned.length === 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "14px 9px",
              fontSize: 12.5,
              color: COLORS.ink3,
            }}
          >
            <MI name="circleCheck" size={16} color={COLORS.green} />
            Nothing waiting on you
          </div>
        )}
        {returned.map((r) => (
          <button
            key={r.id}
            onClick={() => {
              setBellAnchor(null);
              dispatch({ type: "SET_ROLE", role: "writer" });
              onContinueReport(r);
            }}
            style={{
              width: "100%",
              border: 0,
              background: "none",
              textAlign: "left",
              padding: "8px 9px",
              borderRadius: 6,
              cursor: "pointer",
              display: "flex",
              alignItems: "flex-start",
              gap: 9,
            }}
          >
            <MI name="back" size={15} color={COLORS.amber} />
            <span style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <b style={{ fontSize: 12.5, fontWeight: 500, color: COLORS.ink }}>
                {r.proj} · {r.cust}
              </b>
              <span style={{ fontSize: 11.5, color: COLORS.ink3 }}>
                from {r.backFrom?.toLowerCase()} — {r.backBy}
              </span>
            </span>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10.5,
                color: COLORS.ink3,
                whiteSpace: "nowrap",
              }}
            >
              {r.backAt}
            </span>
          </button>
        ))}
      </Popover>

      {/* row more menu */}
      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
        keepMounted
      >
        {moreRow && (
          <div style={{ minWidth: 230, padding: 6 }}>
            <div
              style={{
                fontSize: 10.5,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: COLORS.ink3,
                padding: "7px 9px 4px",
              }}
            >
              Project {moreRow.proj} · {moreRow.cust}
            </div>
            <MenuItem
              onClick={() => {
                setMoreAnchor(null);

                if (moreRow.is_new_report) {
                  onCreateReport(moreRow);
                } else {
                  onContinueReport(moreRow);
                }
              }}
            >
              <MI name="fileText" size={15} style={{ marginRight: 9 }} />
              Open report
            </MenuItem>
            <MenuItem onClick={() => setMoreAnchor(null)}>
              <MI name="notes" size={15} style={{ marginRight: 9 }} />
              Report notes
              <span style={{ marginLeft: "auto", color: COLORS.ink3 }}>4</span>
            </MenuItem>
            <MenuItem onClick={() => setMoreAnchor(null)}>
              <MI name="checkbox" size={15} style={{ marginRight: 9 }} />
              Tasks
              <span style={{ marginLeft: "auto", color: COLORS.ink3 }}>2</span>
            </MenuItem>
            {role === "writer" && (
              <MenuItem
                onClick={() => {
                  setMoreAnchor(null);
                  setChecklistRow(moreRow);
                }}
              >
                <MI name="Checklist" size={15} style={{ marginRight: 9 }} />
                CheckList
                <span style={{ marginLeft: "auto", color: COLORS.ink3 }}>
                  {ctl.ckItems.length}
                </span>
              </MenuItem>
            )}
          </div>
        )}
      </Menu>
      {ChecklistRow && (
        <CheckList
          id={ChecklistRow.id}
          open={true}
          onClose={() => setChecklistRow(null)}
          is_writer={true}
          is_superuser={false}
          refetch={() => {}}
        />
      )}
      <NotesDialog
        open={Boolean(notesRow)}
        onClose={() => setNotesRow(null)}
        row={notesRow}
      />
      <PdfPreviewDialog
        open={pdfOpen}
        pdfUrl={pdfUrl}
        fileName={pdfFileName}
        onClose={closePdf}
      />
    </>
  );
};
